import React from 'react';
import { Formation, Competence } from '../../../../../../shared/types/config';

interface FormationsTabProps {
  formations: Formation[];
  searchFormation: string;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  onEdit: (formation: Formation) => void;
  onDelete: (id: number) => void;
  competences: Competence[];
}

const FormationsTab: React.FC<FormationsTabProps> = ({
  formations,
  searchFormation,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  competences,
}) => {
  const filtered = formations.filter((f) => {
    const q = searchFormation.toLowerCase();
    return (
      f.nom.toLowerCase().includes(q) ||
      (f.description || '').toLowerCase().includes(q) ||
      (f.competence_nom || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Rechercher une formation..."
            value={searchFormation}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60"
          />
          <svg className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <button
          onClick={onAdd}
          className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-[#1a2537]">
            <tr>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">ID</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Ordre</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Nom</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Compétence obtenue</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Durée</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Prix</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Statut</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-cockpit-text-2">
                  Aucune formation trouvée
                </td>
              </tr>
            ) : (
              filtered.map((f) => {
                const comp = competences.find((c) => c.id === f.config_competence_id);
                const compNom = comp?.nom || f.competence_nom || null;
                return (
                  <tr key={f.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                    <td className="px-4 py-3 text-cockpit-text-2">{f.id}</td>
                    <td className="px-4 py-3 text-cockpit-text-2">
                      <span className="px-2 py-1 rounded text-xs font-mono border border-[#223046] bg-[#0b1220] text-cockpit-text-2">{f.ordre}</span>
                    </td>
                    <td className="px-4 py-3 text-cockpit-text font-medium">
                      {f.nom}
                      {f.description && (
                        <p className="text-cockpit-text-2 text-xs mt-0.5 truncate max-w-xs">{f.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {compNom ? (
                        <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-300 border border-blue-500/40">
                          {compNom}
                        </span>
                      ) : (
                        <span className="text-cockpit-muted text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-cockpit-text-2 whitespace-nowrap">{f.duree_heures}h</td>
                    <td className="px-4 py-3 text-cockpit-text-2 whitespace-nowrap">
                      {Number(f.prix) > 0 ? `${Number(f.prix).toLocaleString()} €` : 'Gratuit'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold border ${
                        f.actif
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                          : 'bg-red-500/20 text-red-300 border-red-500/40'
                      }`}>
                        {f.actif ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(f)}
                          className="rounded border border-[#6366f1]/40 bg-[#6366f1]/20 p-1.5 text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => f.id && onDelete(f.id)}
                          className="rounded border border-red-500/35 bg-red-500/10 p-1.5 text-red-300 hover:bg-red-500/20 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FormationsTab;
